/**
 * Validates if a file matches the given accept criteria (MIME types and/or file extensions)
 * @param file - The File object to validate
 * @param acceptString - The accept string from an input element (e.g., "image/*,.pdf,application/json")
 * @returns boolean indicating if the file is valid
 */
export const validateFileAgainstAccept = (
    file: File,
    acceptString: string,
): boolean => {
    if (!acceptString.trim()) {
        return true; // No restrictions if accept string is empty
    }

    const acceptedTypes = acceptString
        .split(",")
        .map((type) => type.trim().toLowerCase());
    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();

    return acceptedTypes.some((acceptedType) => {
        // Handle file extensions like .pdf, .doc, etc.
        if (acceptedType.startsWith(".")) {
            return fileName.endsWith(acceptedType);
        }

        // Handle wildcard MIME types like image/*, video/*, etc.
        if (acceptedType.includes("/*")) {
            const baseType = acceptedType.split("/")[0];
            return fileType.startsWith(baseType + "/");
        }

        // Handle exact MIME types like application/pdf, image/jpeg, etc.
        return fileType === acceptedType;
    });
};

/**
 * Validates file size against maximum allowed size
 * @param file - The File object to validate
 * @param maxSizeInBytes - Maximum allowed file size in bytes
 * @returns boolean indicating if the file size is valid
 */
export const validateFileSize = (
    file: File,
    maxSizeInBytes: number,
): boolean => {
    return file.size <= maxSizeInBytes;
};

/**
 * Converts bytes to human-readable format
 * @param bytes - Number of bytes
 * @returns Formatted string (e.g., "1.5 MB", "500 KB")
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Gets file extension from filename
 * @param filename - The filename to extract extension from
 * @returns File extension with dot (e.g., ".pdf") or empty string if no extension
 */
export const getFileExtension = (filename: string): string => {
    const lastDotIndex = filename.lastIndexOf(".");
    if (lastDotIndex === -1 || lastDotIndex === filename.length - 1) {
        return "";
    }
    return filename.substring(lastDotIndex).toLowerCase();
};

/**
 * Extracts content type from URL query parameters
 * Useful for URLs with response-content-type parameter (e.g., S3 signed URLs)
 * @param url - The URL to extract content type from
 * @returns The content type string or undefined if not found
 */
export const extractContentTypeFromUrl = (url?: string): string | undefined => {
    if (!url) {
        return undefined;
    }

    try {
        const parsed = new URL(url);
        const contentType = parsed.searchParams.get("response-content-type");
        return contentType ? decodeURIComponent(contentType) : undefined;
    } catch {
        // Return undefined if URL parsing fails
        return undefined;
    }
};
